import React, { useCallback, useEffect } from 'react';

import cn from 'classnames';
import { isNull } from 'lodash';
import { bool, string } from 'prop-types';

import {
  EL_SCRIPT_SRC,
  SCRIPT_SELECTORS,
  SCRIPT_SRC,
} from 'components/Common/GoogleTranslateV2/constants';

import { debounceIntervalLong } from 'constants/debounceInterval';
import useMediaInfo from 'hooks/useMediaInfo';
import useUserRedux from 'hooks/useUserRedux';
import { isNonEmptyArray } from 'utils/arrays';
import { loadScript } from 'utils/dom';

import s from './GoogleTranslate.css';

function initGoogleTranslate(isElementary) {
  const layoutCode = window.innerWidth < 1270 ? 0 : 2;
  const paramsBase = {
    pageLanguage: 'en',
    layout: layoutCode,
    autoDisplay: false,
    gaTrack: true,
    gaId: 'UA-43742130-1',
  };

  const allLanguages =
    'sq,am,ar,hy,bn,bs,bg,zh-CN,zh-TW,nl,en,tl,fr,de,el,gu,ht,iw,hi,hmn,ig,ilo,it,ja,km,ko,lo,ml,ne,ps,fa,pl,pt,pt-PT,pa,ro,ru,sr,sd,so,es,sw,ta,te,th,tr,uk,ur,vi,yo,rw,my,mn';
  const allParams = {
    ...paramsBase,
    includedLanguages: isElementary ? 'en,es' : allLanguages,
  };

  // eslint-disable-next-line no-new
  new window.google.translate.TranslateElement(
    allParams,
    'google_translate_element',
  );
}

function initGoogleTranslateElementary() {
  initGoogleTranslate(true);
}

window.initGoogleTranslateElementary = initGoogleTranslateElementary;
window.initGoogleTranslate = initGoogleTranslate;

/**
 * @param {string} className Class of the translate button
 * @param {boolean<false>} hidden If true, we hide the translate button from it's placement
 * @param {boolean<false>} isElementary If true, we load scripts correctly for elementary users
 * @param {boolean<false>} isOnboarding If true, we import different style set for the google translate button
 * @returns @GoogleTranslate
 */
const GoogleTranslate = ({ className, hidden, isElementary, isOnboarding }) => {
  const { isMobile } = useMediaInfo();
  const { isStudent, isCounselor, isGuardian, isOAuth2Authenticated } =
    useUserRedux();

  const scriptSrc = isElementary ? EL_SCRIPT_SRC : SCRIPT_SRC;

  const reApplyScript = useCallback(() => {
    // Select the element that's embedded into DOM via script
    const scriptEl = document.querySelector(`script[src="${scriptSrc}"]`);

    // If element already exist, we remove the previously embedded element, to be able to create a new one
    if (scriptEl) {
      scriptEl.parentNode.removeChild(scriptEl);
    }

    // Iterate through script element selectors and remove previously embedded ones into DOM
    SCRIPT_SELECTORS.forEach(selector => {
      const elementToRemove = Array.prototype.slice.call(
        document.querySelectorAll(selector),
      );

      if (isNonEmptyArray(elementToRemove)) {
        elementToRemove.forEach(element => {
          element.parentNode.removeChild(element);
        });
      }
    });

    // Embed new element google translate element into DOM via the script
    loadScript(scriptSrc);
  }, [scriptSrc]);

  // we import correct css stylesheet depending on user type or if user is being onboarded
  useEffect(() => {
    if (isOnboarding) {
      import('components/Common/googleTranslateStyles/onboardingStyles.css');
    } else if (isStudent || isCounselor || isGuardian) {
      import(
        'components/Common/googleTranslateStyles/sideAndTopBarTranslateStyles.css'
      );
    } else if (isOAuth2Authenticated) {
      import(
        'components/Common/googleTranslateStyles/topBarTranslateStyles.css'
      );
    } else {
      import(
        'components/Common/googleTranslateStyles/defaultGoogleTranslateStyles.css'
      );
    }
  }, [isStudent, isCounselor, isGuardian, isOAuth2Authenticated, isOnboarding]);

  useEffect(() => {
    // Set a debounced loader by 1s, to be able to re-apply script to refresh translated items if they haven't.
    const debouncedLoader = setTimeout(() => {
      const translateButton = document.querySelector(
        '.skiptranslate.goog-te-gadget',
      );

      if (isNull(translateButton) && !hidden) {
        reApplyScript();
      }
    }, debounceIntervalLong);

    // Clear the timeout when we close the google translator
    return () => clearTimeout(debouncedLoader);
  }, [hidden, reApplyScript]);

  return (
    // disabled because Google Translate widget is deprecated
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      id="google_translate_element"
      aria-label="google_translate_element"
      className={cn(
        className,
        { [s.hidden]: hidden },
        { [s.shown]: !hidden },
        { [s.mobile]: isMobile },
      )}
      role="button"
      tabIndex={0}
      onClick={e => e.stopPropagation()}
    />
  );
};

GoogleTranslate.propTypes = {
  className: string,
  hidden: bool,
  isElementary: bool,
  isOnboarding: bool,
};

GoogleTranslate.defaultProps = {
  className: undefined,
  hidden: false,
  isElementary: false,
  isOnboarding: false,
};

export default GoogleTranslate;
